import { createContext, ReactNode, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import * as api from '@/features/backlog/api';
import { useAppData } from '@/app-data';
import { useIdentity } from '@/features/identity/identity';

/** Default width (px) of the docked side drawer. The member can drag it wider/narrower (see
 *  DrawerDock's resize handle); the live width lives in the `--dock-w` CSS variable on <html>, and
 *  both the dock and the AppLayout content inset read the clamped `--dock-w-eff` (index.scss), so the
 *  board reflows to sit exactly beside the panel — even mid-drag, without re-rendering React. */
export const DOCK_WIDTH = 440;
/** Narrowest the dock can be dragged. */
export const DOCK_MIN = 360;
/** Room always left for the board beside the dock (mirrors `--dock-w-eff`'s max in index.scss). */
const DOCK_BOARD_MIN = 480;

/** Clamp a dock width to [DOCK_MIN, viewport − DOCK_BOARD_MIN] (never below the default's room). */
export const clampDockWidth = (w: number): number => {
  const max = Math.max(DOCK_WIDTH, (typeof window !== 'undefined' ? window.innerWidth : 1920) - DOCK_BOARD_MIN);
  return Math.round(Math.min(Math.max(w, DOCK_MIN), max));
};

/** Write the live dock width to the CSS variable both the dock and the content inset read. */
export const applyDockWidth = (w: number) => {
  document.documentElement.style.setProperty('--dock-w', `${w}px`);
};

// Only dock on wide screens; a narrow viewport can't spare 440px, so it falls back to the
// modal presentation even when the panel is pinned.
const DOCK_MQ = '(min-width: 1024px)';

// Fast-paint cache for the pinned preference. The DB (per-member) is the source of truth
// — this only avoids a first-frame flicker before the fetch resolves, and lets a toggle
// feel instant offline.
const PIN_CACHE_KEY = 'spark-board.drawerPinned';
// Same fast-paint cache for the dragged dock width.
const WIDTH_CACHE_KEY = 'spark-board.dockWidth';

/** Defaults applied to the details panel's create form (a column's status, a parent, a type, and
 *  the epic to adopt — e.g. the epic the rail is focused on when you create from that view). */
type CreateOpts = { initialStatus?: string; initialParentId?: string | null; initialType?: string; initialEpicId?: string | null };

/** One level in the details-panel navigation stack — an item (or a create form), plus which tab it
 *  opens on. Opening a sub-task from within a drawer PUSHES a new entry; Back pops it. */
type PanelEntry = { itemId: string | null; initialTab?: string } & CreateOpts;
type PanelState = { open: boolean; stack: PanelEntry[] };

type ItemPanelCtx = {
  /** Whether the details panel is showing (as a modal, or a dock when `docked`). */
  open: boolean;
  /** The item shown, or null in create mode. */
  itemId: string | null;
  initialStatus?: string;
  initialParentId?: string | null;
  /** Type the create form should default to (e.g. 'epic' from the epic sidebar's New button). */
  initialType?: string;
  /** Epic the create form should adopt (the rail's focused epic when creating from a filtered view). */
  initialEpicId?: string | null;
  /** Tab the drawer should open on (e.g. 'comments' from a row's comments button). */
  initialTab?: string;
  /** The item the views should highlight — the shown item, or null when closed / creating. */
  selectedId: string | null;
  /** User preference (persisted per member): keep details open as a side dock. */
  pinned: boolean;
  /** True when the panel should render as a side dock right now (pinned AND wide screen). */
  docked: boolean;
  /** True when the dock is actually on screen: docked AND not the transiently-hidden empty state.
   *  The board reserves its width off this (not `docked`), so a hidden empty dock reclaims the space. */
  dockVisible: boolean;
  /** Hide the empty (no-selection) dock so the board reclaims the space. Auto-restored the moment an
   *  item/create opens, so clicking a card brings the dock back. */
  hideEmpty: () => void;
  /** Whether docking is even available at this viewport (drives the pin toggle's visibility). */
  canDock: boolean;
  /** Open an existing item, optionally on a specific tab (deep-links from a comments button).
   *  Replaces the current stack — a fresh navigation. */
  openItem: (id: string, tab?: string) => void;
  /** Push an item ON TOP of the current one (a sub-task opened from within a drawer), so Back
   *  returns to it. Starts a fresh stack if the panel was closed. */
  pushItem: (id: string, tab?: string) => void;
  openCreate: (opts?: CreateOpts) => void;
  close: () => void;
  /** Pop the top of the stack (back to the item that opened it); closes the panel at the root. */
  back: () => void;
  /** How many items are stacked — >1 means a Back affordance should show. */
  stackDepth: number;
  setPinned: (v: boolean) => void;
  togglePinned: () => void;
  /** The member's preferred dock width (px, persisted per member). The on-screen width is this
   *  clamped to the viewport by CSS (`--dock-w-eff`). */
  dockWidth: number;
  /** Commit a new dock width (end of a drag / a keyboard step / reset): clamps, caches, persists. */
  setDockWidth: (w: number) => void;
};

const Ctx = createContext<ItemPanelCtx | null>(null);

export const useItemPanel = (): ItemPanelCtx => {
  const v = useContext(Ctx);
  if (!v) throw new Error('useItemPanel must be used within ItemPanelProvider');
  return v;
};

/** Ring for the card/row currently shown in the details panel — the same accent cue as an
 *  open context menu (see ContextMenu usages), so "what am I looking at" reads identically.
 *  `relative z-10` lifts it over neighbours' hairlines; the glow is painted by
 *  `.item-selected::after` (see index.scss) so it never fights an element's own box-shadow. */
export const SELECTED_CLS = 'item-selected relative z-10';

/** Subscribe to a media query, re-rendering when it flips. */
const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches);
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);
  return matches;
};

// The panel starts closed. A shared `?item=<KEY>` link is opened by the active view
// (Table/Kanban/Timeline) via useItemUrlSync once the board's items have loaded — that's what maps
// the friendly key in the URL to the item id this panel wants, so it can't be seeded synchronously here.
const initialPanel = (): PanelState => ({ open: false, stack: [] });

const cachedPinned = (): boolean => {
  try {
    // Default to the docked side drawer for everyone; only an explicit unpin ('0') opts back to the modal.
    return localStorage.getItem(PIN_CACHE_KEY) !== '0';
  } catch {
    return true;
  }
};
const cachePinned = (v: boolean) => {
  try {
    localStorage.setItem(PIN_CACHE_KEY, v ? '1' : '0');
  } catch {
    /* ignore unavailable storage */
  }
};

const cachedDockWidth = (): number => {
  try {
    const n = Number(localStorage.getItem(WIDTH_CACHE_KEY));
    return Number.isFinite(n) && n >= DOCK_MIN ? n : DOCK_WIDTH;
  } catch {
    return DOCK_WIDTH;
  }
};
const cacheDockWidth = (w: number) => {
  try {
    localStorage.setItem(WIDTH_CACHE_KEY, String(w));
  } catch {
    /* ignore unavailable storage */
  }
};

/**
 * App-wide state for the item details panel: which item is open, whether it's a modal or a
 * docked side drawer, and the persisted pin preference. Lifting it here (rather than owning
 * a `drawer` state per view) is what lets the dock stay open across a view switch and keeps
 * a single "selected item" the views can highlight.
 */
export const ItemPanelProvider = ({ children }: { children: ReactNode }) => {
  const { me } = useIdentity();
  const { currentBoardId } = useAppData();
  const [state, setState] = useState<PanelState>(initialPanel);
  const [pinned, setPinnedState] = useState<boolean>(cachedPinned);
  // The STORED preference — never re-clamped on a window resize (a narrower window must not shrink
  // what a wider one restores; `--dock-w-eff` clamps the on-screen width instead). The CSS var
  // mirrors it; DrawerDock writes the var directly while dragging and commits on release.
  const [dockWidth, setDockWidthState] = useState<number>(cachedDockWidth);
  useLayoutEffect(() => applyDockWidth(dockWidth), [dockWidth]);
  // Transiently hide the docked panel's empty (no-selection) state so the board reclaims its width.
  // Cleared the instant anything opens (below), so clicking a card brings the dock straight back.
  const [emptyHidden, setEmptyHidden] = useState(false);
  const wide = useMediaQuery(DOCK_MQ);

  // Switching boards drops the current selection so the (pinned) panel returns to its empty
  // state rather than showing a task from the board you just left. The ref skips the initial
  // null→first-board resolution, so a view that deep-links `?item=` (opening the panel once its
  // items load) isn't immediately closed by that first board resolution.
  const prevBoard = useRef<string | null>(currentBoardId);
  useEffect(() => {
    if (prevBoard.current !== null && prevBoard.current !== currentBoardId) {
      setState({ open: false, stack: [] });
    }
    prevBoard.current = currentBoardId;
  }, [currentBoardId]);

  // True once the user has toggled the pin in THIS session, so a slow mount reconcile can't
  // land after and revert their choice to the stale server value (a last-writer race).
  const userTouchedRef = useRef(false);
  // Same guard for the dock width.
  const widthTouchedRef = useRef(false);

  // Load the persisted preference from the DB (source of truth) once the actor is known.
  // The cached seed above already painted; this reconciles (e.g. a preference set on
  // another device). A failure keeps the cached value.
  useEffect(() => {
    let cancelled = false;
    // A fresh identity should accept its own stored preference even if a previous identity
    // toggled this session; within one identity, a local toggle wins over the in-flight GET.
    userTouchedRef.current = false;
    widthTouchedRef.current = false;
    api
      .getMyPreferences()
      .then((p) => {
        if (cancelled) return;
        if (!widthTouchedRef.current && typeof p.dock_width === 'number' && p.dock_width >= DOCK_MIN) {
          setDockWidthState(p.dock_width);
          cacheDockWidth(p.dock_width);
        }
        if (userTouchedRef.current || typeof p.drawer_pinned !== 'boolean') return;
        setPinnedState(p.drawer_pinned);
        cachePinned(p.drawer_pinned);
      })
      .catch(() => {
        /* keep the cached value */
      });
    return () => {
      cancelled = true;
    };
  }, [me?.id]);

  const setPinned = useCallback((v: boolean) => {
    userTouchedRef.current = true;
    setPinnedState(v);
    cachePinned(v);
    api.saveMyPreferences({ drawer_pinned: v }).catch(() => {
      /* best-effort; the cache holds it until the next successful write */
    });
  }, []);
  // Toggle reads the latest value via a ref so the callback stays stable and the DB write
  // never runs inside a state updater (which StrictMode would double-invoke in dev).
  const pinnedRef = useRef(pinned);
  pinnedRef.current = pinned;
  const togglePinned = useCallback(() => setPinned(!pinnedRef.current), [setPinned]);

  // Commit a dock width. The DB write is debounced so a run of keyboard steps saves once.
  const widthSaveTimer = useRef<number | undefined>(undefined);
  const setDockWidth = useCallback((w: number) => {
    const next = clampDockWidth(w);
    widthTouchedRef.current = true;
    setDockWidthState(next);
    applyDockWidth(next); // even when unchanged — a drag may have left the var at an unclamped value
    cacheDockWidth(next);
    window.clearTimeout(widthSaveTimer.current);
    widthSaveTimer.current = window.setTimeout(() => {
      api.saveMyPreferences({ dock_width: next }).catch(() => {
        /* best-effort; the cache holds it until the next successful write */
      });
    }, 400);
  }, []);

  // `emptyHidden` is STICKY: opening a card still shows the dock WITH its content (dockVisible gates on
  // `open || !emptyHidden`), but closing it returns to hidden — so once you hide the empty dock you
  // never see the empty state again this session (a reload restores the default empty dock).
  const openItem = useCallback((id: string, tab?: string) => setState({ open: true, stack: [{ itemId: id, initialTab: tab }] }), []);
  const pushItem = useCallback(
    (id: string, tab?: string) =>
      setState((s) => ({ open: true, stack: [...(s.open ? s.stack : []), { itemId: id, initialTab: tab }] })),
    [],
  );
  const openCreate = useCallback((opts: CreateOpts = {}) => setState({ open: true, stack: [{ itemId: null, ...opts }] }), []);
  const close = useCallback(() => setState((s) => ({ ...s, open: false })), []);
  const hideEmpty = useCallback(() => setEmptyHidden(true), []);
  const back = useCallback(
    () => setState((s) => (s.stack.length > 1 ? { open: true, stack: s.stack.slice(0, -1) } : { ...s, open: false })),
    [],
  );

  const docked = pinned && wide;
  // The empty state can be transiently hidden; with an item open the dock is always shown.
  const dockVisible = docked && (state.open || !emptyHidden);
  const top = state.stack[state.stack.length - 1] as PanelEntry | undefined;
  const selectedId = state.open ? top?.itemId ?? null : null;

  const value = useMemo<ItemPanelCtx>(
    () => ({
      open: state.open,
      itemId: top?.itemId ?? null,
      initialStatus: top?.initialStatus,
      initialParentId: top?.initialParentId,
      initialType: top?.initialType,
      initialEpicId: top?.initialEpicId,
      initialTab: top?.initialTab,
      selectedId,
      pinned,
      docked,
      dockVisible,
      hideEmpty,
      canDock: wide,
      openItem,
      pushItem,
      openCreate,
      close,
      back,
      stackDepth: state.stack.length,
      setPinned,
      togglePinned,
      dockWidth,
      setDockWidth,
    }),
    [state, top, selectedId, pinned, docked, dockVisible, hideEmpty, wide, openItem, pushItem, openCreate, close, back, setPinned, togglePinned, dockWidth, setDockWidth],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};
